// Client-side matching engine for the inventory reconciliation wizard (spec §4).
// It runs against the fleet dump served by
// GET /inventory_reconciliations/scope_units and decides, for every scanned
// token and every expected unit, which preview group it belongs to. The server
// independently validates the decisions at confirm time — nothing here is
// trusted, it only drives what the operator sees.

export const ON_HAND_STATUSES = ["incoming", "active", "damaged", "temporarily_reactivated"];
// "Gone" is exactly the complement of on-hand (spec §4) — keep the two lists in
// lockstep, they define both the expected set and the reactivation group.
export const GONE_STATUSES = ["lost", "broken", "retired", "donated", "sold"];
export const ALL_STATUSES = [
  "incoming",
  "active",
  "damaged",
  "lost",
  "broken",
  "retired",
  "donated",
  "sold",
  "temporarily_reactivated",
];

// Statuses render Title Case everywhere (spec §5).
const STATUS_LABELS = { temporarily_reactivated: "Temp Reactivated" };
export const statusLabel = (s) =>
  STATUS_LABELS[s] || s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// The per-reconciliation miss action (spec §5) is an enum on the header, not a
// status: it names the *default target* for miss rows. "Leave" deliberately has
// no target, so miss rows fall back to the explicit "Choose status…" affordance.
export const MISS_ACTION_STATUSES = { retire: "retired", mark_lost: "lost", leave: null };
export const missActionStatus = (missAction) => MISS_ACTION_STATUSES[missAction] ?? null;

// structured SKU: alphanumeric segments joined by hyphens (1VDJ-100-3-4)
const SKU_RE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/i;
const NUM_RE = /^\d+$/;

export const isOnHand = (u) => ON_HAND_STATUSES.includes(u.status);
export const isGone = (u) => GONE_STATUSES.includes(u.status);
// An outside-scope row can be pulled into the reconciliation's branch iff the
// unit does not live there already — regardless of item type (spec-addendum
// §A1). Wrong-type units already homed here have nothing to move, so they get
// no transfer affordance and stay status-only.
export const isTransferable = (unit, scope) => unit.branchId !== scope.branchId;
const stripZeros = (s) => s.replace(/^0+/, "") || "0";

// ---- input parsing -------------------------------------------------------

// One pasted blob / CSV → tokens. Tolerates what real count sheets contain:
// float artifacts, tallies, notes, blank cells. Junk (non-numeric, non-SKU) is
// kept and reported rather than dropped (spec §3).
export function parseInput(text) {
  const tokens = [];
  for (const line of text.split(/\r?\n/)) {
    for (let cell of line.split(/[,\t;]/)) {
      cell = cell.trim();
      if (!cell) continue;
      const value = cell.replace(/\.0+$/, ""); // Excel float artifacts: 12303.0
      if (SKU_RE.test(value) && !NUM_RE.test(value)) {
        tokens.push({ raw: cell, value, kind: "sku" });
      } else if (NUM_RE.test(value)) {
        tokens.push({ raw: cell, value, kind: "barcode" });
      } else {
        tokens.push({ raw: cell, value, kind: "junk" });
      }
    }
  }
  return tokens;
}

// ---- unit indexes --------------------------------------------------------

export function buildIndexes(units) {
  const byBarcode = new Map();
  const byStripped = new Map();
  const bySku = new Map();
  for (const u of units) {
    if (u.barcode) {
      push(byBarcode, u.barcode, u);
      push(byStripped, stripZeros(u.barcode), u);
    }
    if (u.sku) push(bySku, u.sku.toLowerCase(), u);
  }
  return { byBarcode, byStripped, bySku };
}

const push = (map, key, v) => {
  const arr = map.get(key);
  if (arr) arr.push(v);
  else map.set(key, [v]);
};

// ---- matching (spec §4) --------------------------------------------------

const inScope = (u, scope) => u.branchId === scope.branchId && u.itemType === scope.type;

// Ambiguity ladder: item type (any branch) → in-branch → sole on-hand.
// Each rung only applies if it leaves ≥1 candidate.
function ladder(candidates, scope) {
  let cands = candidates;
  const byType = cands.filter((u) => u.itemType === scope.type);
  if (byType.length >= 1) cands = byType;
  const byBranch = cands.filter((u) => u.branchId === scope.branchId);
  if (byBranch.length >= 1) cands = byBranch;
  if (cands.length === 1) return { unit: cands[0], candidates: cands };
  const onHand = cands.filter(isOnHand);
  if (onHand.length === 1) return { unit: onHand[0], candidates: cands };
  return { unit: null, candidates: cands };
}

// One scan token → match outcome. legacy_id is never consulted (spec §4 rule 3).
export function matchToken(token, scope, idx) {
  if (token.kind === "junk") return { ...token, outcome: "junk", candidates: [] };

  let candidates = [];
  let via = null;
  if (token.kind === "sku") {
    candidates = idx.bySku.get(token.value.toLowerCase()) || [];
    via = "rental_sku";
  } else {
    candidates = idx.byBarcode.get(token.value) || [];
    via = "barcode";
    if (candidates.length === 0) {
      // zero-stripped barcodes match IN SCOPE ONLY (spec §4 rule 2)
      candidates = (idx.byStripped.get(stripZeros(token.value)) || []).filter((u) =>
        inScope(u, scope),
      );
      via = "barcode~0";
    }
  }

  if (candidates.length === 0) return { ...token, outcome: "unmatched", via, candidates: [] };
  if (candidates.length === 1)
    return { ...token, outcome: "resolved", via, unit: candidates[0], candidates };
  const picked = ladder(candidates, scope);
  if (picked.unit)
    return { ...token, outcome: "resolved", via, unit: picked.unit, candidates: picked.candidates };
  return { ...token, outcome: "needs_review", via, candidates: picked.candidates };
}

// ---- full reconciliation pass -------------------------------------------

// entries: deduped tokens (+count). reviewPicks: {tokenValue: unitId|'none'}.
export function reconcile({ entries, scope, units, idx, reviewPicks }) {
  const groups = {
    matched: [], // resolved, in scope, on-hand, not on rental
    reactivations: [], // resolved, in scope, gone status
    onRental: [], // resolved, in scope, currently out — auto-skipped
    outsideScope: [], // resolved, wrong branch and/or type
    needsReview: [], // ambiguous after the ladder
    reviewResolved: [], // picked/no-matched review scans leave a tombstone (undo-able)
    unmatched: [], // no candidates (junk + no-hit numbers)
  };
  const resolvedByUnit = new Map(); // unitId -> {unit, scans:[]}

  for (const entry of entries) {
    let m = matchToken(entry, scope, idx);
    if (m.outcome === "needs_review") {
      const pick = reviewPicks[entry.value];
      if (pick === "none") {
        groups.reviewResolved.push({ value: entry.value, kind: entry.kind, pick: "none" });
        m = { ...m, outcome: "unmatched", noMatch: true };
      } else if (pick) {
        const unit = m.candidates.find((u) => u.id === pick);
        if (unit) {
          // where the picked unit lands, so the tombstone can say so
          const groupLabel = !inScope(unit, scope)
            ? "Scanned Here, but Belongs Elsewhere"
            : unit.onRental
              ? "Out on Rental"
              : isGone(unit)
                ? "Scanned but Marked Gone"
                : "Matched & accounted for";
          groups.reviewResolved.push({ value: entry.value, kind: entry.kind, pick, unit, groupLabel });
          m = { ...m, outcome: "resolved", unit, picked: true };
        }
      }
    }
    if (m.outcome === "resolved") {
      const cur = resolvedByUnit.get(m.unit.id);
      if (cur) {
        cur.scans.push(m);
        // `picked` survives so the confirm payload can file the unit under the
        // needs_review_resolved group (spec §7).
        cur.picked = cur.picked || !!m.picked;
      } else {
        resolvedByUnit.set(m.unit.id, { unit: m.unit, scans: [m], picked: !!m.picked });
      }
    } else if (m.outcome === "needs_review") {
      groups.needsReview.push(m);
    } else {
      groups.unmatched.push(m);
    }
  }

  const onRentalById = new Map();
  for (const { unit, scans, picked } of resolvedByUnit.values()) {
    const row = { unit, scans, picked };
    if (!inScope(unit, scope)) groups.outsideScope.push(row);
    else if (unit.onRental) onRentalById.set(unit.id, row);
    else if (isGone(unit)) groups.reactivations.push(row);
    else groups.matched.push(row);
  }

  // Out on rental right now: expected to be off the rack, so they are never
  // missable — and they belong in the preview whether or not the count happened
  // to pick one up (spec §5). Scanned ones keep their scans.
  for (const unit of units) {
    if (!inScope(unit, scope) || !unit.onRental || !isOnHand(unit)) continue;
    if (!onRentalById.has(unit.id)) onRentalById.set(unit.id, { unit, scans: [] });
  }
  groups.onRental = [...onRentalById.values()];

  // misses = expected set (in-scope on-hand incl. incoming, minus on-rental) not scanned
  const scannedIds = new Set(resolvedByUnit.keys());
  const misses = units.filter(
    (u) => inScope(u, scope) && isOnHand(u) && !u.onRental && !scannedIds.has(u.id),
  );
  groups.misses = misses.map((unit) => ({ unit, scans: [] }));

  const expectedCount = units.filter(
    (u) => inScope(u, scope) && isOnHand(u) && !u.onRental,
  ).length;
  const resolvedCount = resolvedByUnit.size;
  const outsideCount = groups.outsideScope.length;
  return {
    groups,
    stats: {
      totalScans: entries.length,
      resolvedCount,
      outsideCount,
      expectedCount,
      // mis-scope: a large share of what matched isn't in the selected scope
      misScope: resolvedCount >= 10 && outsideCount / resolvedCount > 0.3,
      outsideShare: resolvedCount ? outsideCount / resolvedCount : 0,
    },
  };
}

// Miss-set subgroups for bulk dismissal (spec §5): incoming and never-barcoded
// are called out separately, everything else groups by inventory item.
export function missSubgroups(missRows) {
  const special = { incoming: [], neverBarcoded: [] };
  const byItem = new Map();
  for (const row of missRows) {
    if (!row.unit.barcode) special.neverBarcoded.push(row);
    else if (row.unit.status === "incoming") special.incoming.push(row);
    else {
      const key = row.unit.itemName || "(no item)";
      push(byItem, key, row);
    }
  }
  const items = [...byItem.entries()]
    .map(([name, rows]) => ({ name, rows }))
    .sort((a, b) => b.rows.length - a.rows.length);
  return { ...special, items };
}

// ---- staged changes ------------------------------------------------------

// Default target status per row, before per-unit override (spec §5).
export function defaultAction(row, groupKey, missAction) {
  switch (groupKey) {
    case "reactivations":
      return "active";
    case "misses":
      // Never-barcoded units take the miss action too — they are just as absent.
      return missActionStatus(missAction);
    default:
      return null; // matched / outsideScope / onRental: presence confirmed, no change by default
  }
}

// Collapse rows + overrides + decisions into the list of writes the confirm
// step will perform. Only rows explicitly decided 'adjust' stage a write —
// undecided and 'skip' rows are untouched (spec §5).
export function stagedChanges({ result, overrides, decisions, missAction }) {
  const changes = [];
  const collect = (rows, groupKey) => {
    for (const row of rows) {
      if (decisions[row.unit.id] !== "adjust") continue;
      const target = overrides[row.unit.id] ?? defaultAction(row, groupKey, missAction);
      if (target && target !== row.unit.status) {
        changes.push({ unit: row.unit, from: row.unit.status, to: target, groupKey });
      }
    }
  };
  collect(result.groups.matched, "matched");
  collect(result.groups.reactivations, "reactivations");
  collect(result.groups.outsideScope, "outsideScope");
  collect(result.groups.misses, "misses");
  return changes;
}

// Transfer / adjust / skip / undecided tally for a group's rows.
//
// Transfer is the second, independent axis (spec-addendum §A1): a row can be
// transferred and adjusted at once, so it can count in both segments. What it
// can never be is undecided — staging a transfer settles the row on its own
// (§C), which is what keeps the done trail, the footer's undecided total and
// the confirm warning agreeing with each other.
export function decisionCounts(rows, decisions, transfers = {}) {
  const counts = { transfer: 0, adjust: 0, skip: 0, undecided: 0 };
  for (const row of rows) {
    const d = decisions[row.unit.id];
    const transferring = Boolean(transfers[row.unit.id]);
    if (transferring) counts.transfer += 1;
    if (d === "adjust") counts.adjust += 1;
    else if (d === "skip") counts.skip += 1;
    else if (!transferring) counts.undecided += 1;
  }
  return counts;
}
